import React, { useEffect } from "react";
import type { ComponentType } from "react";
import { get, includes, isEmpty, isEqual, reduce } from "lodash";
import { connect, type ConnectedProps } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth0 } from "utils/react-auth0-wrapper";
import {
  activeAccountState,
  isEmployerAdminSelector,
  isEmployerAgentSelector,
  isOnboardingSubsequentAccountSelector,
  isPreKycSubmissionState,
  isRolloverSelector,
  userIsLockedSelector,
  userIsPaychexPerks,
} from "store/selectors/user";
import VerifyEmail from "pages/signUp/VerifyEmail";
import Home from "pages/Home";
import IndividualEnroll from "pages/signUp/individual/IndividualEnroll";
import IndividualInvestmentProfile from "pages/signUp/individual/IndividualInvestmentProfile";
import IndividualDisclosure from "pages/signUp/individual/IndividualDisclosure";
import IndividualTerms from "pages/signUp/individual/IndividualTerms";
import IndividualThanks from "pages/signUp/individual/IndividualThanks";
import IndividualDenied from "pages/signUp/individual/IndividualDenied";
import SetupContribution from "pages/signUp/individual/setupContribution/SetupContribution";
import PerksSetupContribution from "pages/signUp/individual/setupContribution/PerksSetupContribution";
import IndividualDocsRequired from "pages/signUp/individual/IndividualDocsRequired";
import IndividualRequiredUpdates from "pages/dashboards/individualDashboard/requiredUpdates/IndividualRequiredUpdates";
import IndividualPortfolioSelection from "pages/signUp/individual/IndividualPortfolioSelection";
import IndividualAccountType from "../pages/signUp/individual/IndividualAccountType";
import ErrorBoundary from "components/ErrorBoundary";
import queryString from "query-string";
import EmployerEnroll from "pages/signUp/employer/EmployerEnroll";
import EmployerSelfServeController from "pages/signUp/employer/EmployerSelfServeController";
import EmployerTerms from "pages/signUp/employer/EmployerTerms";
import EmployerBilling from "pages/signUp/employer/EmployerBilling";
import {
  AccountApplicationAdditionalApexDocumentationSubmitted,
  AccountApplicationApexAutoAppeal,
  AccountApplicationApexDocumentsRequired,
  AccountApplicationApproved,
  AccountApplicationDenied,
  AccountApplicationSubmitted,
  EmployerAdminActive,
  EmployerAdminLoginCreated,
  employerAdminStates,
  EmployerApplicationApproved,
  EmployerBillingDetailsComplete,
  EmployerBillingDetailsPending,
  EmployerCreated,
  EmployerSelfServeBillingDetailsComplete,
  EmployerSelfServeBillingDetailsPending,
  EmployerSelfServeControllerComplete,
  EmployerSelfServeControllerRegistrationPending,
  IndividualAccountTypeSelectionPending,
  IndividualApplicationAdditionalDocumentationSubmitted,
  IndividualApplicationAdditionalDocumentationSubmittedKYC1,
  IndividualApplicationAppealDocumentationAdminReviewPending,
  IndividualApplicationAppealDocumentationAdminReviewPendingKYC1,
  IndividualApplicationAppealSubmitted,
  IndividualApplicationAppealSubmittedKYC1,
  IndividualApplicationApproved,
  IndividualApplicationAttestationSubmitted,
  IndividualApplicationDenied,
  IndividualApplicationInfoComplete,
  IndividualApplicationRequiresAdditionalDocumentation,
  IndividualApplicationRequiresAdditionalDocumentationForAppeal,
  IndividualApplicationRequiresAdditionalDocumentationForAppealKYC1,
  IndividualApplicationRequiresAdditionalDocumentationKYC1,
  IndividualApplicationRestartedKYC2,
  IndividualApplicationSubmittedKYC1,
  IndividualApplicationSubmittedKYC2,
  IndividualCompletedForms,
  IndividualContributionPending,
  IndividualDisclosuresComplete,
  IndividualEmailVerificationComplete,
  IndividualInvestmentInfoComplete,
  IndividualInvestmentProfileUpdate,
  IndividualLoginCreated,
  IndividualPayrollContributionPending,
  IndividualPortfolioPending,
  IndividualPortfolioUpdate,
  IndividualRequiredUpdatesPending,
  IndividualSubmitSsnPending,
  IndividualTermsAccepted,
  IndividualTermsPending,
  lifecycleStates,
  onboardingStates,
} from "statics/states";
import NoState from "components/NoState";
import IndividualChangeEmail from "pages/signUp/individual/IndividualChangeEmail";
import EmailVerification from "pages/verification/EmailVerification";
import EmailVerificationRedirect from "pages/verification/EmailVerificationRedirect";
import { requiresAdministrativeUpdates } from "store/selectors/employer";
import EmployerRequiredUpdates from "pages/dashboards/employerDashboard/requiredUpdates/EmployerRequiredUpdates";
import { individualSite } from "../utils/determineSite";
import type { RootState } from "types/state";

// list of known query strings the app may use globally
// typed as a lookup map: keys arrive from queryString.parse (arbitrary strings)
const knownQueryStrings: Record<string, string> = {
  invitationCode: "invitationCode",
  signupCode: "signupCode",
};

// userState/companyState/accountState carry a `state` field the routing logic
// branches on. The redux slice types them as `object`; cast at the boundary in
// mapStateToProps to this shape (mirrors the original propTypes).
interface StateShape {
  state?: string;
}

// Compute the query string fragment for known global query strings (e.g. invitationCode)
function getSafeQueryString() {
  const queryObj = queryString.parse(window.location.search);
  return reduce(
    queryObj,
    (acc, val, key) => {
      if (knownQueryStrings[key]) {
        // Faithful to prior JS: ternary used as a statement to append to acc,
        // and queryString.parse values (string | string[] | null) are coerced
        // by the template literal exactly as before.
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions, @typescript-eslint/restrict-template-expressions
        acc ? (acc += `&${key}=${val}`) : (acc += `?${key}=${val}`);
      }
      return acc;
    },
    ""
  );
}

function showNewAdministratorRoutes(
  userStateObj: StateShape,
  companyStateObj: StateShape,
  isAdmin: boolean,
  isAgent: boolean
) {
  const userState = get(userStateObj, "state", "");
  const companyState = get(companyStateObj, "state", "");

  const isOnboardingAgent = isAgent && employerAdminStates.includes(userState);
  const companyActive = companyState === EmployerApplicationApproved;
  const isOnboardingSecondaryAdmin =
    isAdmin && employerAdminStates.includes(userState) && companyActive;

  return isOnboardingAgent || isOnboardingSecondaryAdmin;
}

// Props a parent passes in. Everything else on the component is redux-injected.
interface PrivateRouteOwnProps {
  component: ComponentType;
  path?: string | string[];
}

const mapStateToProps = (state: RootState) => ({
  // user.userState/companyState are typed `object` on the slice; cast at the
  // boundary to the `state`-bearing shape the routing logic reads.
  userState: state.user.userState as StateShape,
  companyState: state.user.companyState as StateShape,
  userRoleId: state.user.userRoleId,
  fetchingState: state.user.initialUserLoading,
  userIsLocked: userIsLockedSelector(state),
  isEmployerAdmin: isEmployerAdminSelector(state),
  isEmployerAgent: isEmployerAgentSelector(state),
  canChangeEmailOnboarding: isPreKycSubmissionState(state),
  requiresAdministrativeUpdates: requiresAdministrativeUpdates(state),
  isRollover: isRolloverSelector(state),
  isOnboardingSubsequentAccount: isOnboardingSubsequentAccountSelector(state),
  accountState: activeAccountState(state),
  isPaychexPerks: userIsPaychexPerks(state),
});

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type PrivateRouteProps = PropsFromRedux & PrivateRouteOwnProps;

/* eslint-disable react/display-name */
const PrivateRoute = ({
  component: Component,
  userState,
  accountState,
  isRollover,
  isOnboardingSubsequentAccount,
  path = "",
  companyState,
  userRoleId,
  userIsLocked,
  fetchingState,
  isEmployerAdmin,
  isEmployerAgent,
  canChangeEmailOnboarding,
  requiresAdministrativeUpdates,
  isPaychexPerks,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- preserved: rest is collected but unused, as in prior JS
  ...rest
}: PrivateRouteProps) => {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const location = useLocation();

  useEffect(() => {
    const fn = async () => {
      if (!isAuthenticated) {
        const targetUrl = `${location.pathname}${
          location.search ? location.search : ""
        }`;
        // Persist across the Auth0 redirect so we can restore the URL on return
        // without depending on onRedirectCallback + navigate() timing.
        sessionStorage.setItem("auth0_postLoginRedirect", targetUrl);
        await loginWithRedirect({
          appState: {
            targetUrl,
          },
        });
      }
    };
    void fn();
  }, [isAuthenticated, loginWithRedirect, path]);

  // element to render when we're at the right route (no redirect needed)
  let element = null;
  // where to redirect if we're at the wrong route (null means stay)
  let redirectTo = null;

  const safeQueryString = getSafeQueryString();

  const onboardOffAccount = isRollover || isOnboardingSubsequentAccount;

  function navigateTo(newPath: string, preserveQuery = true) {
    redirectTo = newPath + (preserveQuery ? safeQueryString : "");
  }

  function buildOnboardingRoutes() {
    const stateToUse = onboardOffAccount ? accountState : userState.state;

    if (canChangeEmailOnboarding && includes(path, "individual/change-email")) {
      element = <IndividualChangeEmail />;
    } else if (
      canChangeEmailOnboarding &&
      includes(path, "/verify-email-redirect")
    ) {
      element = <EmailVerificationRedirect />;
    } else if (canChangeEmailOnboarding && includes(path, "/verify-email")) {
      element = <EmailVerification />;
    } else {
      switch (stateToUse) {
        case IndividualSubmitSsnPending:
        case IndividualLoginCreated:
          element = <VerifyEmail />;
          if (!includes(path, "/individual/verify")) {
            navigateTo("/individual/verify");
          }
          break;
        case IndividualEmailVerificationComplete:
          element = <IndividualEnroll />;
          if (!includes(path, "/individual/info")) {
            navigateTo("/individual/info");
          }
          break;
        case IndividualApplicationInfoComplete:
          element = <IndividualDisclosure />;
          if (path !== "/individual/disclosure") {
            navigateTo("/individual/disclosure");
          }
          break;
        case IndividualDisclosuresComplete:
          if (path === "/individual/investment") {
            element = <IndividualInvestmentProfile />;
          } else {
            navigateTo("/individual/investment");
          }
          break;
        case IndividualInvestmentInfoComplete:
          if (path === "/individual/investment") {
            element = <IndividualPortfolioSelection />;
          } else {
            navigateTo("/individual/investment");
          }
          break;
        case IndividualPayrollContributionPending:
          // Paychex Flex (Perks) diverges from the standard individual flow
          // only at the contribution step: worker-record split vs bank/group.
          element = isPaychexPerks ? (
            <PerksSetupContribution />
          ) : (
            <SetupContribution />
          );
          if (!includes(path, "/individual/setup_contribution")) {
            navigateTo("/individual/setup_contribution");
          }
          break;
        case IndividualAccountTypeSelectionPending:
          element = <IndividualAccountType />;
          if (path !== "/individual/account_type") {
            navigateTo("/individual/account_type");
          }
          break;
        case IndividualTermsPending:
          element = <IndividualTerms />;
          if (path !== "/individual/terms") {
            navigateTo("/individual/terms");
          }
          break;
        case IndividualApplicationSubmittedKYC1:
        case IndividualApplicationSubmittedKYC2:
        case IndividualApplicationApproved:
        case IndividualTermsAccepted:
        case IndividualApplicationAppealSubmitted:
        case IndividualApplicationAppealSubmittedKYC1:
        case IndividualApplicationAdditionalDocumentationSubmittedKYC1:
        case IndividualApplicationAdditionalDocumentationSubmitted:
        case IndividualApplicationAppealDocumentationAdminReviewPending:
        case IndividualApplicationAppealDocumentationAdminReviewPendingKYC1:
        case IndividualApplicationAttestationSubmitted:
        case IndividualCompletedForms:
        case AccountApplicationSubmitted:
        case AccountApplicationApexAutoAppeal:
        case IndividualApplicationRestartedKYC2:
        case AccountApplicationAdditionalApexDocumentationSubmitted:
          element = <IndividualThanks />;
          if (path !== "/individual/thanks") {
            navigateTo("/individual/thanks");
          }
          break;

        // Nothing left to fill in, so don't pin the member to the success screen —
        // its Continue button takes them to the dashboard.
        case AccountApplicationApproved:
          element =
            path === "/individual/thanks" ? (
              <IndividualThanks />
            ) : (
              <Component />
            );
          break;

        case IndividualApplicationRequiresAdditionalDocumentationKYC1:
        case IndividualApplicationRequiresAdditionalDocumentation:
        case IndividualApplicationRequiresAdditionalDocumentationForAppeal:
        case IndividualApplicationRequiresAdditionalDocumentationForAppealKYC1:
        case AccountApplicationApexDocumentsRequired:
          element = <IndividualDocsRequired />;
          if (path !== "/individual/attention") {
            navigateTo("/individual/attention");
          }
          break;
        case IndividualApplicationDenied:
        case AccountApplicationDenied:
          element = <IndividualDenied />;
          if (path !== "/individual/status") {
            navigateTo("/individual/status");
          }
          break;
      }
    }
  }

  function buildAdministratorRoutes() {
    const isLoginOrSignUpRoute = path === "/" || path === "/login";
    if (
      userState.state === EmployerAdminActive &&
      includes(path, "/employer/verify")
    ) {
      element = <VerifyEmail />;
    } else if (requiresAdministrativeUpdates && isEmployerAdmin) {
      element = <EmployerRequiredUpdates />;
      if (!includes(path, "/required-updates")) {
        navigateTo("/required-updates");
      }
    } else if (
      userState.state === EmployerAdminActive &&
      !includes(path, "/employer/verify") &&
      !isLoginOrSignUpRoute
    ) {
      element = <Component />;
    } else if (
      userState.state === EmployerAdminActive &&
      !includes(path, "/employer/verify") &&
      isLoginOrSignUpRoute
    ) {
      redirectTo = "/dashboard";
      element = <Component />;
    } else if (userState.state === EmployerAdminLoginCreated) {
      element = <VerifyEmail />;
      if (!includes(path, "/employer/verify")) {
        redirectTo = "/employer/verify";
      }
    } else {
      element = <Component />;
    }
  }

  function buildEmployerAdminRoutes() {
    if (userState.state === EmployerAdminLoginCreated) {
      element = <VerifyEmail />;
      if (!includes(path, "/employer/verify")) {
        navigateTo("/employer/verify");
      }
    } else if (requiresAdministrativeUpdates && isEmployerAdmin) {
      element = <EmployerRequiredUpdates />;
      if (!includes(path, "/required-updates")) {
        navigateTo("/required-updates");
      }
    } else if (
      userState.state === EmployerAdminActive &&
      isEmpty(companyState)
    ) {
      if (includes(path, "/employer/verify")) {
        element = <VerifyEmail />;
      } else {
        if (!includes(path, "/employer/enroll")) {
          navigateTo("/employer/enroll");
        }
        element = <EmployerEnroll />;
      }
    } else if (
      !isEmpty(companyState) &&
      companyState.state === EmployerCreated
    ) {
      element = <EmployerTerms />;
      if (!includes(path, "/employer/terms")) {
        navigateTo("/employer/terms");
      }
    } else if (
      !isEmpty(companyState) &&
      companyState.state === EmployerSelfServeControllerRegistrationPending
    ) {
      element = <EmployerSelfServeController />;
      if (!includes(path, "/employer/info")) {
        navigateTo("/employer/info");
      }
    } else if (
      (!isEmpty(companyState) &&
        companyState.state === EmployerBillingDetailsPending) ||
      companyState.state === EmployerSelfServeBillingDetailsPending
    ) {
      element = <EmployerBilling />;
      if (!includes(path, "/employer/billing")) {
        navigateTo("/employer/billing");
      }
    } else if (
      (!isEmpty(companyState) &&
        companyState.state === EmployerSelfServeControllerComplete) ||
      companyState.state === EmployerBillingDetailsComplete ||
      companyState.state === EmployerSelfServeBillingDetailsComplete
    ) {
      element = <EmployerTerms />;
      if (!includes(path, "/employer/terms")) {
        navigateTo("/employer/terms");
      }
    } else if (path === "/" || path === "/login") {
      redirectTo = "/dashboard";
      element = <Component />;
    } else {
      element = <Component />;
    }
  }

  function buildLifecycleRoutes() {
    if (path === "/" || path === "/login") {
      redirectTo = "/dashboard";
      element = <Component />;
    } else if (location.pathname === "/dashboard/documents") {
      element = <Component />;
    } else if (IndividualRequiredUpdatesPending === userState.state) {
      element = <IndividualRequiredUpdates />;
      if (!includes(path, "/required-updates")) {
        navigateTo("/required-updates");
      }
    } else if (IndividualInvestmentProfileUpdate === userState.state) {
      if (path.includes("/individual/investment")) {
        element = <IndividualInvestmentProfile />;
      } else {
        redirectTo = "/individual/investment";
      }
    } else if (
      [IndividualPortfolioPending, IndividualPortfolioUpdate].includes(
        // userState.state is optional on StateShape; cast at the boundary
        // (includes(undefined) is a safe no-op, matching prior JS behavior)
        userState.state as string
      )
    ) {
      element = <IndividualPortfolioSelection />;
      if (!includes(path, "/individual/portfolio_selection")) {
        navigateTo("/individual/portfolio_selection");
      }
    } else if (userState.state === IndividualContributionPending) {
      element = <SetupContribution />;
      if (!includes(path, "/individual/setup_contribution")) {
        navigateTo("/individual/setup_contribution");
      }
    } else if (includes(path, "/individual")) {
      redirectTo = "/dashboard";
      element = <Component />;
    } else {
      element = <Component />;
    }
  }

  const stateToUse = onboardOffAccount ? accountState : userState.state;

  if (isAuthenticated !== true) {
    console.log("Error displaying route. No Auth.");
    return null;
  }
  if (!fetchingState && !userState.state) {
    if (individualSite()) {
      if (path === "/login") {
        return <Component />;
      } else {
        return <Navigate to={"/login" + safeQueryString} replace />;
      }
    }
    console.log("Error displaying route. No State.");
    return <NoState />;
  }
  if (userIsLocked && location.pathname !== "/dashboard") {
    return <Navigate to="/dashboard" replace />;
  } else if (
    path === "/link-bank" &&
    // userState.state/stateToUse are optionally undefined on their shapes;
    // cast at the boundary (includes(undefined) is a safe no-op, as in prior JS)
    (employerAdminStates.includes(userState.state as string) ||
      lifecycleStates.includes(userState.state as string))
  ) {
    element = <Component />;
  } else if (onboardingStates.includes(stateToUse as string)) {
    buildOnboardingRoutes();
  } else if (
    showNewAdministratorRoutes(
      userState,
      companyState,
      isEmployerAdmin,
      isEmployerAgent
    )
  ) {
    buildAdministratorRoutes();
  } else if (employerAdminStates.includes(userState.state as string)) {
    buildEmployerAdminRoutes();
  } else if (
    lifecycleStates.includes(stateToUse as string) &&
    userRoleId !== "4"
  ) {
    buildLifecycleRoutes();
  } else {
    if (fetchingState) {
      element = null;
    } else if (userState.state === "login" || isEmpty(userState)) {
      console.log("Route to login.");
      element = <Home />;
      redirectTo = "/login" + safeQueryString;
    } else {
      element = <Component />;
    }
  }

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }
  return <ErrorBoundary>{element}</ErrorBoundary>;
};

const propsEqual = (
  prevProps: Readonly<PrivateRouteProps>,
  nextProps: Readonly<PrivateRouteProps>
) => {
  const statesEqual = isEqual(prevProps.userState, nextProps.userState);
  const accountStatesEqual = isEqual(
    prevProps.accountState,
    nextProps.accountState
  );
  const pathsEqual = isEqual(prevProps.path, nextProps.path);
  const companyStateEqual = isEqual(
    prevProps.companyState,
    nextProps.companyState
  );
  const fetchingStateEqual = isEqual(
    prevProps.fetchingState,
    nextProps.fetchingState
  );
  return (
    statesEqual &&
    pathsEqual &&
    companyStateEqual &&
    accountStatesEqual &&
    fetchingStateEqual
  );
};

export default connector(React.memo(PrivateRoute, propsEqual));
